import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Search, Sparkles, Loader2, Check, Home as HomeIcon } from "lucide-react";
import { API } from "./api";
import { VoiceTextBox } from "./AIAssist";
import { useAuth } from "./authApi";

const inp = "w-full bg-white/[0.04] border border-white/12 px-4 py-2.5 text-sm text-white placeholder-zinc-300 outline-none focus:border-[#C19A6B] focus:bg-white/[0.06] transition-colors rounded-[5px]";

export default function AcquereurModal({ open, onClose }) {
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const [raw, setRaw] = useState("");
  const [contact, setContact] = useState({ first_name: "", email: "", phone: "" });
  const [recap, setRecap] = useState(null);
  const [editSummary, setEditSummary] = useState("");
  const [recapLoading, setRecapLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k, v) => setContact((c) => ({ ...c, [k]: v }));

  const close = () => { onClose(); };

  const genRecap = async () => {
    if (raw.trim().length < 10) { setErr("Décrivez votre recherche en quelques mots."); return; }
    setErr(""); setRecapLoading(true);
    try {
      const res = await fetch(`${API}/acquereurs/summary`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ raw_text: raw }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Erreur");
      setRecap(d);
      setEditSummary((d.summary || "").trim());
    } catch (e) { setErr(typeof e.message === "string" ? e.message : "Génération impossible"); }
    setRecapLoading(false);
  };

  const submit = async () => {
    setErr("");
    if (raw.trim().length < 10) { setErr("Décrivez votre recherche en quelques mots."); return; }
    if (!isAuthed && !contact.email) { setErr("Votre e-mail est requis pour être alerté."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/acquereurs`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...contact, last_name: "", password: "", raw_text: raw, summary: editSummary.trim(), anonymous: false }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Erreur");
      setDone(d);
    } catch (e) { setErr(typeof e.message === "string" ? e.message : "Envoi impossible"); }
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close} data-testid="acquereur-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[845px] max-h-[92vh] overflow-y-auto rounded-[5px] border border-white/15 bg-white/[0.07] backdrop-blur-md shadow-2xl text-white p-5 sm:p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div aria-hidden="true" className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#C19A6B]/20 blur-3xl pointer-events-none" />
            <button onClick={close} data-testid="acquereur-modal-close" className="absolute top-4 right-4 z-10 text-zinc-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>

            {done ? (
              <div className="text-center py-6" data-testid="acquereur-modal-done">
                <Check className="w-14 h-14 text-white mx-auto mb-4" />
                <p className="calli text-3xl text-[#C19A6B] leading-none">Merci {done.display_name} !</p>
                <p className="text-sm text-zinc-300 mt-4 max-w-lg mx-auto leading-relaxed">Votre recherche est enregistrée. Nos conseillers vous alerteront en priorité dès qu'un bien off market correspond à vos critères.</p>
                {done.summary && <p className="text-sm text-zinc-300 italic mt-4 border-l-2 border-[#C19A6B] pl-4 text-left max-w-lg mx-auto">« {done.summary} »</p>}
                <button onClick={close} data-testid="acquereur-modal-ok" className="mt-6 inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 rounded-[5px] transition-colors"><HomeIcon className="w-4 h-4" /> Fermer</button>
              </div>
            ) : (
              <>
                <div className="px-2">
                  <p className="calli text-3xl text-[#C19A6B] leading-none">Décrivez le bien de vos rêves</p>
                  <p className="text-sm text-zinc-300 mt-2.5 leading-relaxed">Décrivez votre bien idéal, à la voix ou au clavier, comme si vous en parliez à un ami. Notre IA capte vos envies et part à la chasse aux <strong className="font-semibold text-white">pépites off market</strong> qui matchent vraiment votre projet. Résultat : une sélection <strong className="font-semibold text-white">taillée sur-mesure</strong>, et vous êtes prévenu <strong className="font-semibold text-white">en avant-première</strong>, avant tout le monde.</p>
                </div>

                <div className="mt-4 text-left">
                  <VoiceTextBox value={raw} onChange={(v) => { setRaw(v); setRecap(null); }} placeholder="Ex : maison familiale avec jardin à Chantilly, budget 600-750k, 4 chambres, au calme et proche des écoles…" testid="acq-modal-voice" minHeight="min-h-[72px]" micLabel="Recherche vocale IA" />
                </div>

                <button type="button" onClick={genRecap} disabled={recapLoading} data-testid="acq-modal-recap-btn" className="mt-3 inline-flex items-center gap-2 bg-white hover:bg-zinc-200 text-[#050505] disabled:opacity-60 text-xs uppercase tracking-wider px-4 py-2 rounded-[5px] transition-colors">
                  {recapLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyse…</> : <><Sparkles className="w-3.5 h-3.5" /> Générer le récapitulatif IA</>}
                </button>

                {recap && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-3 rounded-[5px] border border-[#C19A6B]/30 bg-white/[0.04] p-3 text-left" data-testid="acq-modal-recap">
                    <p className="text-[10px] uppercase tracking-wider text-[#C19A6B] mb-1.5 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Résumé — modifiable avant enregistrement</p>
                    <textarea value={editSummary} onChange={(e) => setEditSummary(e.target.value)} data-testid="acq-modal-summary-edit" rows={3}
                      className="w-full bg-white/[0.05] border border-white/15 rounded-[5px] px-3 py-2 text-xs text-white outline-none focus:border-[#C19A6B] resize-none" />
                    <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
                      {recap.budget && <span className="text-[10px] uppercase tracking-wide text-[#C19A6B] border border-[#C19A6B]/30 px-2 py-0.5 rounded-[5px]">Budget : {recap.budget}</span>}
                      {(recap.secteurs || []).map((x, i) => <span key={`s${i}`} className="text-[10px] text-zinc-300 border border-white/15 px-2 py-0.5 rounded-[5px]">{x}</span>)}
                      {(recap.type_bien || []).map((x, i) => <span key={`t${i}`} className="text-[10px] text-zinc-300 border border-white/15 px-2 py-0.5 rounded-[5px]">{x}</span>)}
                    </div>
                  </motion.div>
                )}

                {!isAuthed && (
                  <div className="grid sm:grid-cols-3 gap-2.5 mt-4 text-left">
                    <input className={inp} placeholder="Prénom" value={contact.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="acq-modal-first" />
                    <input className={inp} type="email" placeholder="E-mail *" value={contact.email} onChange={(e) => set("email", e.target.value)} data-testid="acq-modal-email" />
                    <input className={inp} placeholder="Téléphone" value={contact.phone} onChange={(e) => set("phone", e.target.value)} data-testid="acq-modal-phone" />
                  </div>
                )}

                {err && <p className="text-sm text-red-400 mt-3" data-testid="acq-modal-error">{err}</p>}

                <div className="flex flex-col items-center gap-2.5 mt-4">
                  <button onClick={submit} disabled={loading} data-testid="acq-modal-submit" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-7 py-3 rounded-[5px] transition-colors">
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Search className="w-4 h-4" /> {isAuthed ? "Enregistrer ma recherche" : "Être alerté en priorité"}</>}
                  </button>
                  <Link to="/espace-acquereurs" onClick={close} data-testid="acq-modal-full" className="text-xs text-zinc-400 hover:text-[#C19A6B] transition-colors">Voir l'espace acquéreur complet →</Link>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
